/**
 * Завершение мероприятия: итог словами и черта, по которую считать.
 *
 * Мероприятие кончается — этим оно и отличается от всего остального в консоли.
 * Но до сих пор закончить его было нечем: метод `campaigns.save` существовал с
 * первого дня, а кнопки, которая бы его звала, не было ни на одном экране.
 * Переезд прошёл 29 августа и три недели стоял «идёт», собирая в свою ленту
 * каждую ночную копию: 293 строки той ночи и 705 чужих поверх неё. Владелец:
 * «переезд не закрыт на портале и копит события».
 *
 * Поэтому здесь две вещи, и обе обязательные. Итог — потому что через год
 * ответит он, а не память. Момент окончания — потому что закрывают обычно не в
 * ту минуту, когда кончилось: лента обрезается по названной черте, и на экране
 * видно, по какой.
 *
 * События при этом не трогаются. Схлопывается и обрезается ПОКАЗ; сами записи
 * — единственная несочинённая история работы, и стирать из неё неудобное
 * значит завтра не суметь ответить, сколько раз проверка отвечала «нет».
 */
import { useState } from 'react';
import { useQuery } from '../transport/hooks.ts';
import { request } from '../transport/socket.ts';

interface Campaign {
  id: string;
  state: string;
  outcome?: string;
  createdAt: string;
}

/** `2026-08-29T11:15` — то, что понимает `input[type=datetime-local]`. */
function forInput(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    + `T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function CampaignFinish({ id }: { id: string }): React.JSX.Element | null {
  const { data, reload } = useQuery<{ items: Campaign[] }>('campaigns.list');
  const camp = data?.items.find((c) => c.id === id) ?? null;

  const [outcome, setOutcome] = useState('');
  const [at, setAt] = useState(() => forInput(new Date()));
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  /*
   * Закрытый итог можно переписать.
   *
   * Первую редакцию итога почти всегда пишут наспех и не тем человеком, который
   * будет его читать через год. Итог, который нельзя поправить, переписывают
   * рядом — в заметке, в письме, в голове, — и настоящая история оказывается не
   * там, где её положили.
   *
   * Состояние при этом не трогается: «проведено» обратно в «идёт» не
   * превращается, и черта ленты остаётся прежней.
   */
  const [fixing, setFixing] = useState(false);

  if (!camp) return null;

  const closed = camp.state === 'проведено' || camp.state === 'отменено';
  if (closed && !fixing) {
    return (
      <section className="wizard__step">
        <h3 className="wizard__title">Чем кончилось</h3>
        <p className="muted">
          Мероприятие <b>{camp.state}</b>. Лента ниже закрыта и больше не растёт.
        </p>
        {camp.outcome
          ? <p className="camp__outcome">{camp.outcome}</p>
          : <p className="muted">Итог не записан — а через год отвечать будет он.</p>}
        <button type="button" className="button button--ghost button--small"
                onClick={() => { setOutcome(camp.outcome ?? ''); setFixing(true); }}>
          поправить итог
        </button>
      </section>
    );
  }

  async function finish(state: 'проведено' | 'отменено'): Promise<void> {
    setBusy(true); setFailure(null);
    try {
      await request('campaigns.save', {
        id,
        ...(closed
          /* Правка итога — только итог: черту и состояние оставляем как есть. */
          ? {}
          : { state, finishedAt: new Date(at).toISOString() }),
        outcome: outcome.trim(),
      });
      setFixing(false);
      reload();
    } catch (err) {
      setFailure((err as { msg?: string }).msg ?? 'не получилось');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="wizard__step">
      <h3 className="wizard__title">{closed ? 'Поправить итог' : 'Завершить мероприятие'}</h3>
      <p className="muted">
        {closed
          ? 'Меняется только текст итога: состояние и черта ленты остаются прежними.'
          : 'Пока мероприятие не закрыто, его лента собирает всё подряд: ночные копии, '
            + 'чужие восстановления, письма. Закрытое — перестаёт расти и остаётся тем, '
            + 'чем было.'}
      </p>

      {failure && <p className="alert alert--error">{failure}</p>}

      <label className="field">
        <span className="field__label">Чем кончилось</span>
        <textarea
          className="input" rows={5} maxLength={4000}
          placeholder="Что вышло, чем подтверждено и что осталось открытым"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
        />
      </label>

      {!closed && (
        <>
      <label className="field">
        <span className="field__label">Когда кончилось</span>
        <input type="datetime-local" className="input"
               value={at} onChange={(e) => setAt(e.target.value)} />
      </label>
      {/*
        Отдельной строкой, потому что это единственное неочевидное место экрана.

        «Сейчас» правильно только в тот раз, когда закрывают по горячим следам.
        Во все остальные — черта не там, и лента молча оставляет себе лишние
        недели.
      */}
      <p className="muted">
        Лента мероприятия обрежется по этому моменту. Если оно кончилось раньше,
        чем про это вспомнили, — поставьте тот момент, а не сегодняшний. События
        при этом остаются на месте: меняется показ, а не история.
      </p>
        </>
      )}

      <div className="wizard__row">
        <button type="button" className="button"
                disabled={busy || outcome.trim().length < 10}
                onClick={() => { void finish('проведено'); }}>
          {closed ? 'Сохранить итог' : 'Отметить проведённым'}
        </button>
        <button type="button" className="button button--ghost"
                disabled={busy}
                onClick={() => {
                  if (closed) { setFixing(false); return; }
                  void finish('отменено');
                }}>
          {closed ? 'не надо' : 'Мероприятие отменено'}
        </button>
      </div>
      {outcome.trim().length < 10 && (
        <p className="muted">
          Итог обязателен: «проведено» без единого слова о том, что вышло, —
          это не история, а галочка.
        </p>
      )}
    </section>
  );
}
